/**
 * Locations — addressable places within a branch.
 *
 * The API requires exactly one parent, branch or warehouse, and rejects both
 * or neither. Only the branch parent is offered here, because that is the
 * organizational sense of a location; warehouse storage bins are created from
 * the inventory side, where the stock context is.
 *
 * Arabic and English (C7): every word from the catalogs.
 */
import { StatusBadge } from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { OrgListPage } from "./OrgListPage";
import { addressLine } from "./types";
import type { BranchRow, LocationRow } from "./types";

export function LocationsPage() {
  const { t, i18n } = useTranslation();
  const branches = useApiList<BranchRow>(["org", "branches"], "/branches");

  return (
    <OrgListPage<LocationRow>
      title={t("org.locations.title")}
      subtitle={t("org.locations.subtitle")}
      breadcrumb={[t("org.section"), t("org.locations.title")]}
      path="/locations"
      queryKey="locations"
      createLabel={t("org.locations.new")}
      emptyTitle={t("org.locations.emptyTitle")}
      emptyMessage={t("org.locations.emptyMessage")}
      parent={{
        label: t("fields.branch"),
        field: "branchId",
        options: (branches.data?.items ?? []).map((b) => ({
          id: b.id,
          label: `${b.name} (${b.code})`,
        })),
        isLoading: branches.isLoading,
        missingMessage: t("org.locations.missingParent"),
      }}
      columns={[
        { key: "name", label: t("fields.name") },
        {
          key: "code",
          label: t("fields.code"),
          render: (r) => <span className="atlas-mono">{r.code}</span>,
        },
        {
          key: "branch_id",
          label: t("fields.branch"),
          render: (r) =>
            branches.data?.items.find((b) => b.id === r.branch_id)?.name ?? "—",
        },
        {
          key: "address",
          label: t("fields.address"),
          render: (r) => addressLine(r.address, i18n.language),
        },
        {
          key: "status",
          label: t("fields.status"),
          render: (r) => <StatusBadge status={r.status} type="Location" />,
        },
      ]}
    />
  );
}
